import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Storefront, Plus, CaretRight } from '@phosphor-icons/react';
import { MarketplaceCard } from '../components/MarketplaceCard';
import { API, CATEGORY_LABELS, CATEGORY_SEO } from '../utils/marketplace';
import { setPageMeta, breadcrumbLd } from '../utils/seo';

export default function MarketplaceCategory() {
  const { slug } = useParams();
  const seo = CATEGORY_SEO[slug];
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!seo) return;
    setLoading(true);
    axios.get(`${API}/api/marketplace/listings`, { params: { category: slug, limit: 24 } })
      .then((res) => { setListings(res.data.listings); setTotal(res.data.total); })
      .catch(() => setListings([]))
      .finally(() => setLoading(false));

    const origin = window.location.origin;
    setPageMeta({
      title: `${seo.h1} | ConstructHelp Marketplace`,
      description: seo.intro.slice(0, 158),
      url: `${origin}/marketplace/category/${slug}`,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          breadcrumbLd([
            { name: 'Marketplace', url: `${origin}/marketplace` },
            { name: CATEGORY_LABELS[slug] },
          ]),
          {
            '@type': 'FAQPage',
            mainEntity: (seo.faqs || []).map(([q, a]) => ({
              '@type': 'Question', name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
        ],
      },
    });
    window.scrollTo(0, 0);
  }, [slug, seo]);

  if (!seo) return <Navigate to="/marketplace" replace />;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-16" data-testid="marketplace-category-page">
      {/* Header */}
      <div className="bg-[#0F172A] border-b-4 border-black py-10">
        <div className="max-w-6xl mx-auto px-4">
          <nav className="text-xs text-neutral-400 mb-3 flex items-center gap-1" data-testid="category-breadcrumbs">
            <Link to="/marketplace" className="hover:text-[#D95800] font-bold">Marketplace</Link>
            <CaretRight size={10} weight="bold" />
            <span className="text-neutral-300 font-bold">{CATEGORY_LABELS[slug]}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight" data-testid="category-h1">
            {seo.h1}
          </h1>
          <p className="text-neutral-400 mt-3 text-sm md:text-base max-w-3xl">{seo.intro}</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link
              to="/marketplace/new"
              className="inline-flex items-center gap-2 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform"
              data-testid="category-sell-btn"
            >
              <Plus size={16} weight="bold" /> Sell in this category
            </Link>
            <Link
              to={`/marketplace?category=${slug}`}
              className="inline-flex items-center gap-2 bg-white text-[#0F172A] font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#2B4C6F]"
              data-testid="category-filter-link"
            >
              Search & filter
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <p className="text-xs text-neutral-500" data-testid="category-count">{total} active listing{total === 1 ? '' : 's'}</p>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-neutral-200 dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-700 rounded-xl aspect-[3/4] animate-pulse" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16" data-testid="category-empty">
            <Storefront size={56} weight="duotone" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <p className="font-black uppercase text-neutral-700 dark:text-neutral-300">No {CATEGORY_LABELS[slug]} listed right now</p>
            <p className="text-sm text-neutral-500 mt-1 mb-4">Have some to sell? You'll be the only listing buyers see.</p>
            <Link to="/marketplace/new" className="inline-flex items-center gap-2 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000]">
              <Plus size={16} weight="bold" /> List an item free
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4" data-testid="category-grid">
            {listings.map((l) => <MarketplaceCard key={l._id} listing={l} />)}
          </div>
        )}

        {/* FAQs */}
        {seo.faqs?.length > 0 && (
          <div className="mt-12" data-testid="category-faqs">
            <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">Common questions</h2>
            <div className="space-y-3">
              {seo.faqs.map(([q, a]) => (
                <div key={q} className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4">
                  <h3 className="font-bold text-sm text-neutral-900 dark:text-white">{q}</h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1.5 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Other categories */}
        <div className="mt-12" data-testid="category-crosslinks">
          <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">Browse other categories</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(CATEGORY_LABELS).filter(([k]) => k !== slug).map(([k, label]) => (
              <Link
                key={k}
                to={`/marketplace/category/${k}`}
                className="text-xs font-black uppercase px-3 py-1.5 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-2 border-black rounded-full hover:bg-[#D95800] hover:text-white transition-colors"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
